/**
 * Reset Password page
 * Allows users to set a new password using a reset token
 */
/* eslint-disable max-lines-per-function */

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LumiLogo } from "../../shared/components/branding/LumiLogo";
import { useToast } from "../../shared/hooks/use-toast";

interface ApiResponse {
  message?: string;
}

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Validate token exists
  useEffect(() => {
    if (!token) {
      setError("Invalid or missing reset token. Please request a new password reset link.");
    }
  }, [token]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return "Password must be at least 8 characters";
    }
    return null;
  };

  const handlePasswordChange = (pwd: string) => {
    setPassword(pwd);
    setPasswordError(validatePassword(pwd));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitForm();
  };

  const submitForm = async () => {
    if (!token) {
      setError("Invalid or missing reset token");
      return;
    }

    // Validate password
    const pwdError = validatePassword(password);
    if (pwdError) {
      setPasswordError(pwdError);
      return;
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const apiBase = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

    try {
      const res = await fetch(`${apiBase}/v1/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      if (res.ok) {
        // Success - redirect to sign in with success message
        toast({
          description: "Password reset successfully!",
          variant: "default",
        });
        setTimeout(() => {
          navigate("/sign-in?reset=success");
        }, 2000);
      } else {
        const data = (await res.json()) as ApiResponse;
        setError(data.message ?? "Failed to reset password. The link may be expired.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <LumiLogo className="h-16 w-auto" />
        </div>

        {/* Title */}
        <div className="text-center">
          <h1 className="text-3xl font-bold">Set new password</h1>
          <p className="mt-2 text-gray-400">Enter your new password below</p>
        </div>

        {/* Error message */}
        {error && <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400">{error}</div>}

        {/* Form */}
        {token && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                New Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B33B]"
                placeholder="At least 8 characters"
              />
              {passwordError && <p className="mt-1 text-sm text-red-400">{passwordError}</p>}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B33B]"
                placeholder="Re-enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !!passwordError}
              className="w-full py-3 px-4 bg-[#F3B33B] text-black font-semibold rounded-lg hover:bg-[#F3B33B]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Resetting..." : "Reset password"}
            </button>
          </form>
        )}

        {/* Back to sign in */}
        <div className="text-center text-sm text-gray-400">
          Remember your password?{" "}
          <Link to="/sign-in" className="text-[#F3B33B] hover:underline">
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
